import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  RemoveEditOverlayType,
  AddNewModuleType,
  AddNewLayoutType,
  ChangeLayoutType,
  EditModuleType,
  DefaultThemeType,
  SendUpdatedAppConfig,
  SiteEngineConfigEvents,
  AddNewLayoutModuleType,
  ChangePageInfoType,
  EditLayoutModuleType,
  SendAppConfig,
  SaveWhiteLabelConfig,
  CreateDupInstanceType,
  RevertModuleConfigType,
} from './interfaces';
import { updateConfig } from './store/config';
import { RootState } from './store/store';
import {
  pagesWithSamePath,
  insertAt,
  replaceStringElementInArr,
  handleUpdateLayoutModules,
  handleUpdatePageModules,
} from '@skygroup/shared/utils/siteEngineEditModuleHelpers';

// interface Props {}

const useHandleMessagesFromBO = () => {
  const dispatch = useDispatch();
  const { config } = useSelector((data: RootState) => data.config);

  const [selectedInstance, setSelectedInstance] = useState<string | null>(null);

  useEffect(() => {
    const isEventOriginValid = (origin: string) =>
      origin === process.env.REACT_APP_BO_BASE_URL;

    const handleRemoveEditOverlay = (evData: RemoveEditOverlayType) => {
      setSelectedInstance(null);
    };

    const handleAddNewModule = (evData: AddNewModuleType) => {
      const moduleName = evData.moduleName;
      const updatedPages = config.pages.map((page) => {
        if (
          page.allow_edit !== false &&
          (page.id === evData.pageId ||
            pagesWithSamePath(page.path, evData.path))
        ) {
          const updatedPageModules = insertAt(
            page?.modules,
            evData.index,
            moduleName
          );
          return { ...page, modules: updatedPageModules, id: evData.pageId };
        }
        return page;
      });

      const updatedConfig = {
        ...config,
        modulesConfigs: {
          ...config.modulesConfigs,
          [moduleName]: {
            ...evData?.defaultModuleObj,
            instance: moduleName,
          },
          // [newModuleName]: config?.modulesConfigs?.[evData.module],
        },
        pages: updatedPages,
      };

      handleSendAllConfigs({ type: 'SendAppConfig', updatedConfig });

      // window.parent.postMessage(
      //   {
      //     type: 'UpdateWhiteLabelConfigs',
      //     messageArr: [
      //       {
      //         key: 'modulesConfigs',
      //         value: {
      //           [moduleName]: {
      //             ...evData?.defaultModuleObj,
      //             instance: moduleName,
      //           },
      //         },
      //       },
      //       { key: 'pages', value: updatedPages },
      //     ],
      //   },
      //   '*'
      // );

      return dispatch(updateConfig(updatedConfig));
    };

    const handleAddNewLayoutModule = (evData: AddNewLayoutModuleType) => {
      const currentPage = config.pages.find(
        (page) => page.name === 'Package Info'
      );
      const currentLayout = currentPage?.layout ?? '';

      const cloneLayouts = { ...config.layouts };
      const updatedModules = insertAt(
        cloneLayouts[currentLayout]?.[evData.layoutId],
        evData.index,
        evData.module
      );

      const currentLayout2 = {
        ...cloneLayouts[currentLayout],
        [evData.layoutId]: updatedModules,
      };
      const layouts = { ...config.layouts, [currentLayout]: currentLayout2 };
      const updatedConfig = { ...config, layouts };

      // window.parent.postMessage(
      //   {
      //     type: 'UpdateWhiteLabelConfigs',
      //     messageArr: [
      //       { key: 'layouts', value: { [currentLayout]: currentLayout2 } },
      //     ],
      //   },
      //   '*'
      // );
      dispatch(updateConfig(updatedConfig));
    };

    const handleChangeLayout = (evData: ChangeLayoutType) => {
      const updatedPages = config.pages.map((page) => {
        if (
          page.id === evData.pageId ||
          pagesWithSamePath(page.path, evData.path)
        ) {
          return {
            ...page,
            layout: evData.updatedLayout,
            id: evData.pageId,
          };
        }
        return page;
      });

      const updatedConfig = { ...config, pages: updatedPages };

      handleSendAllConfigs({ type: 'SendAppConfig', updatedConfig });

      // window.parent.postMessage(
      //   {
      //     type: 'UpdateWhiteLabelConfigs',
      //     messageArr: [{ key: 'pages', value: updatedPages }],
      //   },
      //   '*'
      // );

      dispatch(updateConfig(updatedConfig));
    };

    const handleChangePageInfo = (evData: ChangePageInfoType) => {
      const updatedPages = config.pages.map((page) => {
        if (
          page.id === evData.pageId ||
          pagesWithSamePath(page.path, evData.path)
        ) {
          return {
            ...page,
            ...evData.updatedPageInfo,
          };
        }
        return page;
      });

      const updatedConfig = { ...config, pages: updatedPages };
      handleSendAllConfigs({ type: 'SendAppConfig', updatedConfig });
      dispatch(updateConfig(updatedConfig));
    };

    const handleChangeModule = (
      evData: EditModuleType | EditLayoutModuleType
    ) => {
      const { type, ...updatedModulesConfig } = evData;
      if (!Object.keys(updatedModulesConfig).length) {
        // maybe use it as delete operation??
        return;
      }
      const updatedConfig = {
        ...config,
        modulesConfigs: { ...config.modulesConfigs, ...updatedModulesConfig },
      };

      // DEV =>
      handleSendAllConfigs({ type: 'SendAppConfig', updatedConfig });
      // DEV <=

      dispatch(updateConfig(updatedConfig));
    };

    const handleCreateDupInstance = (evData: CreateDupInstanceType) => {
      const {
        type,
        pageId,
        replaceIndex,
        selectedPage,
        selectedModuleInfo,
        selectedModuleUpdated,
        dupModuleObj,
      } = evData;

      const helperFunc = !selectedModuleInfo?.layoutSection
        ? handleUpdatePageModules
        : handleUpdateLayoutModules;

      const layoutsArr = config.layouts;

      const helperProps = {
        prev: config,
        renderedLayoutArr: selectedModuleInfo?.layoutSection
          ? layoutsArr?.[selectedPage?.layout]?.[
              selectedModuleInfo?.layoutSection
            ]
          : null,
        selectedPage,
        selectedModuleInfo,
        newDupInstance: dupModuleObj,
      };

      const updatedPagesOrLayouts = helperFunc(helperProps);

      const updatedConfig = {
        ...config,
        modulesConfigs: {
          ...config.modulesConfigs,
          [dupModuleObj.instance]: dupModuleObj,
        },
        ...(!selectedModuleInfo?.layoutSection
          ? { pages: updatedPagesOrLayouts }
          : { layouts: updatedPagesOrLayouts }),
      };

      // handleSendAllConfigs({ type: 'SendAppConfig', updatedConfig });
      dispatch(updateConfig(updatedConfig));
    };

    const handleRevertModuleConfig = (evData: RevertModuleConfigType) => {
      const { type, isLayoutUpdate, updatedModulesArr } = evData;

      const updatedConfig = {
        ...config,
        ...(!isLayoutUpdate
          ? { pages: updatedModulesArr }
          : { layouts: updatedModulesArr }),
      };

      // handleSendAllConfigs({ type: 'SendAppConfig', updatedConfig });
      dispatch(updateConfig(updatedConfig));
    };

    const handleAddNewLayout = (evData: AddNewLayoutType) => {
      const updatedConfig = {
        ...config,
        layouts: {
          ...config.layouts,
          ...evData.updatedLayouts,
        },
      };
      dispatch(updateConfig(updatedConfig));
    };

    const handleDefaultThemeUpdate = (evData: DefaultThemeType) => {
      const updatedConfig = {
        ...config,
        theme: evData.theme,
      };
      dispatch(updateConfig(updatedConfig));
    };

    const handleSendAllConfigs = (evData: SendAppConfig) => {
      window.parent.postMessage(
        {
          type: 'GetAllConfigs',
          message: { config: evData?.updatedConfig ?? config },
        },
        '*'
      );
    };

    const eventHandler = (event: MessageEvent<any>) => {
      // if (!isValidEventType(event.data.type)) {
      if (!event.data.type) {
        return;
      }
      const evData: SiteEngineConfigEvents = event.data;
      switch (evData.type) {
        // case 'SaveWhiteLabelConfig':
        //   handleSaveWhiteLabelConfigs(evData);
        //   break;
        case 'SendAppConfig':
          handleSendAllConfigs(evData);
          break;
        // case 'SendUpdatedAppConfig':
        //   handleSendUpdatedConfigs(evData);
        //   break;
        case 'RemoveEditOverlay':
          handleRemoveEditOverlay(evData);
          break;
        case 'AddNewModule':
          handleAddNewModule(evData);
          break;
        case 'AddNewLayoutModule':
          handleAddNewLayoutModule(evData);
          break;
        case 'AddNewLayout':
          handleAddNewLayout(evData);
          break;
        case 'ChangeLayout':
          handleChangeLayout(evData);
          break;
        case 'ChangePageInfo':
          handleChangePageInfo(evData);
          break;
        case 'DefaultTheme':
          handleDefaultThemeUpdate(evData);
          break;
        case 'EditLayout':
        case 'EditModule':
          handleChangeModule(evData);
          break;
        case 'CreateDupInstance':
          handleCreateDupInstance(evData);
          break;
        case 'RevertModuleConfig':
          handleRevertModuleConfig(evData);
          break;
        // case 'EditLayout':
        //   handleChangeLayoutModule(evData);
        //   break;
        default:
        // console.log('NO SUCH EVENT');
      }
    };
    window.addEventListener('message', eventHandler);
    return () => window.removeEventListener('message', eventHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  return {
    selectedInstance,
    setSelectedInstance,
  };
};

export default useHandleMessagesFromBO;
